import React from 'react';
import { HelpCircle, Mail, MessageSquare, ExternalLink, ChevronLeft, Globe, Clock, BookOpen } from 'lucide-react';
import { Link } from 'react-router-dom';

const Support = () => {
    const supportCategories = [
        {
            title: "Email Support",
            description: "Dedicated help for technical issues and account recovery.",
            contact: "support@factindianews.com",
            icon: Mail,
            color: "text-blue-500",
            bgColor: "bg-blue-50 dark:bg-blue-900/10"
        },
        {
            title: "Help Desk",
            description: "Internal portal for submitting ID card correction requests.",
            contact: "Portal ID: #ST-992",
            icon: MessageSquare,
            color: "text-emerald-500",
            bgColor: "bg-emerald-50 dark:bg-emerald-900/10"
        },
        {
            title: "Office Contact",
            description: "Visit our administrative office for physical verification.",
            contact: "HQ Admin Desk",
            icon: Globe,
            color: "text-[#D0021B]",
            bgColor: "bg-red-50 dark:bg-red-900/10"
        }
    ];

    const faqItems = [
        {
            q: "How do I update a staff member's photo?",
            a: "Go to the Dashboard, click the 'Edit' icon in the Actions column for the specific card, and upload a new photo."
        },
        {
            q: "What should I do if a card is lost?",
            a: "Deactivate the current card immediately through the dashboard and issue a New Card with a new ID number."
        },
        {
            q: "How do I verify a card's QR code?",
            a: "Scan the QR code printed on the physical card with any mobile device to visit the secure verification link."
        }
    ];

    return (
        <div className="min-h-screen bg-white dark:bg-[#0B0F1A] transition-colors duration-300">
            {/* Header section with back button */}
            <div className="border-b border-gray-100 dark:border-gray-800 bg-white/80 dark:bg-gray-900/80 backdrop-blur-xl sticky top-0 z-10">
                <div className="max-w-4xl mx-auto px-6 py-6 flex items-center justify-between">
                    <Link
                        to="/"
                        className="flex items-center gap-2 text-[#666666] dark:text-gray-400 hover:text-[#D0021B] dark:hover:text-[#FF4D4D] transition-colors font-medium"
                    >
                        <ChevronLeft className="w-5 h-5" />
                        Back to Dashboard
                    </Link>
                    <div className="flex items-center gap-3">
                        <HelpCircle className="w-6 h-6 text-[#D0021B]" />
                        <h1 className="font-serif text-2xl font-bold dark:text-white">Support Center</h1>
                    </div>
                </div>
            </div>

            <div className="max-w-4xl mx-auto px-6 py-12">
                {/* Intro */}
                <div className="text-center mb-16">
                    <h2 className="text-3xl font-serif font-bold text-[#1A1A1A] dark:text-white mb-4">How can we help you today?</h2>
                    <p className="text-[#666666] dark:text-gray-400 max-w-xl mx-auto">
                        Welcome to the Press ID Management Support Center. We're here to ensure your 
                        identity management workflow remains seamless and secure.
                    </p>
                </div>

                {/* Support Grid */}
                <div className="grid md:grid-cols-3 gap-6 mb-16">
                    {supportCategories.map((cat, idx) => (
                        <div key={idx} className={`${cat.bgColor} p-8 rounded-[2rem] border border-white dark:border-gray-800 shadow-sm hover:shadow-xl transition-all hover:-translate-y-1 group`}>
                            <div className={`${cat.color} mb-6 p-3 bg-white dark:bg-gray-800 rounded-2xl w-fit shadow-sm group-hover:rotate-12 transition-transform`}>
                                <cat.icon className="w-6 h-6" />
                            </div>
                            <h3 className="text-lg font-bold text-[#1A1A1A] dark:text-white mb-2">{cat.title}</h3>
                            <p className="text-sm text-[#666666] dark:text-gray-400 mb-6 leading-relaxed">
                                {cat.description}
                            </p>
                            <div className="text-[#D0021B] dark:text-[#FF4D4D] font-bold text-sm">
                                {cat.contact}
                            </div>
                        </div>
                    ))}
                </div>

                {/* FAQ Section */}
                <section className="bg-gray-50 dark:bg-gray-800/20 p-8 md:p-12 rounded-[2.5rem] border border-gray-100 dark:border-gray-800">
                    <div className="flex items-center gap-3 mb-8">
                        <BookOpen className="w-6 h-6 text-[#D0021B]" />
                        <h2 className="text-2xl font-serif font-bold dark:text-white">Frequently Asked Questions</h2>
                    </div>
                    <div className="space-y-8">
                        {faqItems.map((item, idx) => (
                            <div key={idx} className="group">
                                <h3 className="text-lg font-bold text-[#1A1A1A] dark:text-white mb-2 group-hover:text-[#D0021B] transition-colors">
                                    {item.q}
                                </h3>
                                <p className="text-[#666666] dark:text-gray-400 leading-relaxed">
                                    {item.a}
                                </p>
                            </div>
                        ))}
                    </div>
                </section>

                {/* Info Bar */}
                <div className="mt-16 grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="flex items-center gap-4 p-6 bg-white dark:bg-gray-900 border border-gray-100 dark:border-gray-800 rounded-2xl">
                        <Clock className="w-6 h-6 text-[#666666] dark:text-gray-400" />
                        <div>
                            <p className="font-bold dark:text-white">Support Hours</p>
                            <p className="text-sm text-[#666666] dark:text-gray-400">Mon - Fri, 09:00 AM - 06:00 PM IST</p>
                        </div>
                    </div>
                    <div className="flex items-center gap-4 p-6 bg-white dark:bg-gray-900 border border-gray-100 dark:border-gray-800 rounded-2xl">
                        <ExternalLink className="w-6 h-6 text-[#666666] dark:text-gray-400" />
                        <div>
                            <p className="font-bold dark:text-white">System Status</p>
                            <p className="text-sm text-[#666666] dark:text-gray-400">All systems operational</p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Support;
